import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { InvoiceStatus, PaymentBank } from '@/utils/enums';
import { download } from '@/utils/helpers';

const Headings = styled.div`
  margin-bottom: 42px;
`;

const Dialog = styled(StyledDialog)`
  padding-bottom: 24px;
  display: flex;
  flex-direction: column;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  position: absolute;
  top: 24px;
  left: 24px;
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

const StyledButton = styled(Button)`
  font-weight: 600;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.purple};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.gray};
    &:hover,
    &:active {
      border-color: ${({ theme }) => theme.colors.grayscale};
    }
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
`;

const DownloanZipLink = styled.a`
  height: 54px;
  justify-content: center;
  transition: 0.2s ease-out all;
  display: inline-flex;
  align-items: center;
  padding: 0 40px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  ${({ theme }) => css`
    color: ${theme.colors.black};
    border-color: ${theme.colors.grayscale};
    background: ${theme.colors.white};
    &:hover,
    &:active {
      color: ${theme.colors.black};
      border-color: ${({ theme }) => theme.colors.purple};
    }
  `}
`;

// 422 from the Swedbank export: nothing was written, so the invoices stay
// unpaid and the operator needs to know exactly what to go and fix.
interface SwedbankExportProblem {
  message: string;
  invoices_without_company?: number[];
  companies_missing_details?: {
    company_id: number;
    company: string;
    missing: string[];
    invoice_ids: number[];
  }[];
}

const MISSING_DETAIL_LABELS: Record<string, string> = {
  registration_code: 'registration code',
  bank_account: 'bank account',
};

const describeExportProblem = ({
  invoices_without_company: withoutCompany,
  companies_missing_details: missingDetails,
}: SwedbankExportProblem) => {
  const lines: string[] = [];

  if (withoutCompany?.length) {
    lines.push(
      `No company set on invoice${withoutCompany.length === 1 ? '' : 's'} ${withoutCompany.join(', ')}.`,
    );
  }

  missingDetails?.forEach(({ company, missing, invoice_ids: invoiceIds }) => {
    const missingText = missing
      .map((detail) => MISSING_DETAIL_LABELS[detail] ?? detail)
      .join(' and ');

    lines.push(
      `${company} is missing a ${missingText} — invoice${invoiceIds.length === 1 ? '' : 's'} ${invoiceIds.join(', ')}.`,
    );
  });

  return lines.join('\n');
};

interface Props {
  onClose: () => void;
  invoicesIds: number[];
  invoicesIdsToMakePaid: number[];
  setSelectedInvoices: () => void;
  selectedPaymentTypes?: string[];
}

const InvoicesExportModal: FC<Props> = ({
  onClose,
  invoicesIds,
  invoicesIdsToMakePaid,
  setSelectedInvoices,
  selectedPaymentTypes,
}) => {
  const now = DateTime.now().toFormat('yyyy-LL-dd');

  const [showBankSelection, setShowBankSelection] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const ref = useRef<HTMLDivElement>(null);

  const onBack = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowBankSelection(false);
  };

  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const makePaid = async (paidByBank: PaymentBank) => {
    if (!invoicesIdsToMakePaid.length) return;
    await api.put<
      { invoices: number[]; status: InvoiceStatus; paid_by_bank: PaymentBank },
      { invoices: number[]; status: InvoiceStatus; paid_by_bank: PaymentBank }
    >(`/v1/payments/invoices/status`, {
      invoices: invoicesIdsToMakePaid,
      status: InvoiceStatus.Paid,
      paid_by_bank: paidByBank,
    });
  };

  const handlePostExport = () => {
    queryClient.invalidateQueries(['invoices_list'], { exact: false });
    setSelectedInvoices();
    onClose();
    alerts.close();
  };

  const isSwedbankTypeDisabled = (type: string) =>
    selectedPaymentTypes?.length ? !selectedPaymentTypes.includes(type) : false;

  const onRevolutExport = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to export it as CSV',
      );
      if (!confirmed) return;

      const csvs = await api.get<never, { [key: string]: string }>(
        `/v1/payments/revolut-export`,
        { params: { invoices: invoicesIds.join(',') } },
      );

      // One file per company and currency, keyed `{company short}-{currency}`.
      Object.entries(csvs).forEach(([fileKey, csv]) => {
        download(
          csv,
          `${fileKey}-${DateTime.now().toFormat('yyyy-LL-dd-HH-mm')}.csv`,
          'application/octetstream',
          false,
        );
      });

      await makePaid(PaymentBank.REVOLUT);
      handlePostExport();
    } catch (err) {
      alerts.error();
    }
  };

  const onPaypalExport = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to export it as CSV',
      );
      if (!confirmed) return;

      const csvs = await api.get<never, { [key: string]: string }>(
        `/v1/payments/paypal-export`,
        { params: { invoices: invoicesIds.join(',') } },
      );

      // One file per company, keyed `{company short}`, plus `no-company` for
      // the invoices that have none set.
      Object.entries(csvs).forEach(([fileKey, csv]) => {
        download(
          csv,
          `${now}-paypal-${fileKey}.csv`,
          'application/octetstream',
          false,
        );
      });

      await makePaid(PaymentBank.PAYPAL);
      handlePostExport();
    } catch (err) {
      alerts.error();
    }
  };

  const onSwedbankExport = async (
    type: 'european' | 'international' | 'domestic',
  ) => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to export it as Xml',
      );
      if (!confirmed) return;

      const xmls = await api.get<never, { [key: string]: string }>(
        `/v1/payments/swedbank-export/${type}`,
        { params: { invoices: invoicesIds.join(',') } },
      );

      // One file per company and currency, keyed `{company short}-{currency}`.
      Object.entries(xmls).forEach(([fileKey, xml]) => {
        download(xml, `${now}-swedbank-${fileKey}-${type}.xml`);
      });

      await makePaid(PaymentBank.SWEDBANK);
      handlePostExport();
    } catch (err) {
      // Nothing was exported on a 422, and makePaid is never reached, so the
      // invoices stay unpaid. Show what's missing instead of the generic error.
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const problem = err.response.data as SwedbankExportProblem;

        return alerts.error(problem.message, describeExportProblem(problem));
      }

      alerts.error();
    }
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        ref={ref}
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        {showBankSelection && (
          <BackButton onClick={onBack}>
            <svg
              width="12"
              height="10"
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.15623 0.805768C4.53964 0.408261 5.1727 0.396836 5.57021 0.78025C5.96772 1.16366 5.97914 1.79673 5.59573 2.19423L3.85578 3.99813H10.5C11.0523 3.99813 11.5 4.44585 11.5 4.99813C11.5 5.55042 11.0523 5.99813 10.5 5.99813H3.85492L5.59573 7.80292C5.97914 8.20043 5.96772 8.83349 5.57021 9.21691C5.1727 9.60032 4.53964 9.58889 4.15623 9.19139L0.781687 5.69281C0.703472 5.61172 0.641629 5.52072 0.596158 5.42395C0.535269 5.29476 0.501228 5.15042 0.501228 4.99813C0.501228 4.71756 0.616779 4.46397 0.802889 4.28236L4.15623 0.805768Z"
                fill="currentColor"
              />
            </svg>
          </BackButton>
        )}
        <CloseButton onClick={onCloseWithEventPrevention}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Headings>
          {showBankSelection ? (
            <Text $type="heading2">Choose type:</Text>
          ) : (
            <Text $type="heading2">Export:</Text>
          )}
        </Headings>
        <List>
          {showBankSelection ? (
            <>
              <StyledButton
                type="button"
                color="secondary"
                onClick={onRevolutExport}
              >
                Revolut
              </StyledButton>
              <StyledButton
                type="button"
                color="secondary"
                onClick={onPaypalExport}
              >
                PayPal
              </StyledButton>
              <StyledButton
                type="button"
                color="secondary"
                disabled={isSwedbankTypeDisabled('Domestic')}
                onClick={() => onSwedbankExport('domestic')}
              >
                Swedbank (Domestic)
              </StyledButton>
              <StyledButton
                type="button"
                color="secondary"
                disabled={isSwedbankTypeDisabled('European')}
                onClick={() => onSwedbankExport('european')}
              >
                Swedbank (European)
              </StyledButton>
              <StyledButton
                type="button"
                color="secondary"
                disabled={isSwedbankTypeDisabled('International')}
                onClick={() => onSwedbankExport('international')}
              >
                Swedbank (International)
              </StyledButton>
            </>
          ) : (
            <>
              <DownloanZipLink
                type="button"
                color="secondary"
                href={`${
                  process.env.REACT_APP_API_ENDPOINT
                }/invoices?invoices=${invoicesIds.join(',')}`}
                download
              >
                Invoices (.zip)
              </DownloanZipLink>
              <StyledButton
                type="button"
                color="secondary"
                onClick={() => setShowBankSelection(true)}
              >
                Payment files (.csv, .xml)
              </StyledButton>
            </>
          )}
        </List>
      </Dialog>
    </Modal>
  );
};

export default InvoicesExportModal;
